import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api-client";

interface LearnedRule {
  ruleId: string;
  fieldType: string;
  description: string;
  category: string | null;
  hitCount: number;
  createdAt: string;
  lastAppliedAt: string | null;
}

interface LearnedRulesResponse {
  rules: LearnedRule[];
  fetchedAt: string;
}

// WU-5.3 — learned rules are TENANT-WIDE: the OCR feedback-rules payload has
// no job-type/profile discriminator, so this panel cannot scope its list to
// the job type whose tab it lives in. The UI says so explicitly instead of
// implying a scoping that doesn't exist.
export function LearnedRulesPanel() {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState<LearnedRulesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mutatingIds, setMutatingIds] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<{ ruleId: string; description: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await api.get<LearnedRulesResponse>("/ocr/learned-rules");
      setData(json);
    } catch (err) {
      if (err instanceof ApiError) {
        const bodyErr = (err.body as { error?: string } | null)?.error;
        setError(bodyErr ?? `HTTP ${err.status}`);
      } else {
        console.warn("LearnedRulesPanel fetch failed", err);
        setError("ocr_unreachable");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (expanded && !data && !loading) {
      void load();
    }
  }, [expanded, data, loading, load]);

  const handleDisable = useCallback(
    async (ruleId: string) => {
      if (!data) return;
      const snapshot = data.rules;
      setMutatingIds((prev) => new Set(prev).add(ruleId));
      setData((prev) =>
        prev ? { ...prev, rules: prev.rules.filter((r) => r.ruleId !== ruleId) } : prev,
      );
      try {
        await api.post(`/ocr/learned-rules/${ruleId}/disable`);
      } catch (err) {
        console.error("Failed to disable rule", err);
        setData((prev) => (prev ? { ...prev, rules: snapshot } : prev));
      } finally {
        setMutatingIds((prev) => {
          const next = new Set(prev);
          next.delete(ruleId);
          return next;
        });
      }
    },
    [data],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingDelete || !data) return;
    const { ruleId } = pendingDelete;
    const snapshot = data.rules;
    setPendingDelete(null);
    setMutatingIds((prev) => new Set(prev).add(ruleId));
    setData((prev) =>
      prev ? { ...prev, rules: prev.rules.filter((r) => r.ruleId !== ruleId) } : prev,
    );
    try {
      await api.delete(`/ocr/learned-rules/${ruleId}`);
    } catch (err) {
      console.error("Failed to delete rule", err);
      setData((prev) => (prev ? { ...prev, rules: snapshot } : prev));
    } finally {
      setMutatingIds((prev) => {
        const next = new Set(prev);
        next.delete(ruleId);
        return next;
      });
    }
  }, [pendingDelete, data]);

  const ruleCount = data?.rules.length ?? 0;

  return (
    <section className="learned-rules-panel" aria-label="Learned rules">
      <button
        type="button"
        className="learned-rules-panel__toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        {/* "tenant-wide" lives in the always-visible label: the collapsed
            state must not imply job-type scoping either. */}
        {expanded ? "\u25BC" : "\u25B6"} Learned Rules {"\u2014"} tenant-wide{data ? ` (${ruleCount})` : ""}
      </button>

      {expanded && (
        <div className="learned-rules-panel__body">
          <p className="learned-rules-panel__scope-note">
            Learned rules apply tenant-wide — this list is not scoped to this job type.
          </p>

          {loading && <div className="learned-rules-panel__loading" role="status">Loading…</div>}

          {error === "ocr_unreachable" && (
            <div className="learned-rules-panel__error" role="alert">
              OCR service unavailable. Retrying…
              <button type="button" onClick={() => void load()}>
                Retry
              </button>
            </div>
          )}

          {error && error !== "ocr_unreachable" && (
            <div className="learned-rules-panel__error" role="alert">
              {error}
              <button type="button" onClick={() => void load()}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && data && ruleCount === 0 && (
            <div className="learned-rules-panel__empty">
              No learned rules yet. Rules are synthesized automatically as operators correct OCR mistakes.
            </div>
          )}

          {!loading && !error && ruleCount > 0 && (
            <table className="learned-rules-panel__table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Field</th>
                  <th>Applied</th>
                  <th>Category</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data!.rules.map((r) => (
                  <tr key={r.ruleId} data-testid={`rule-row-${r.ruleId}`}>
                    <td>{r.description}</td>
                    <td>{r.fieldType}</td>
                    <td>{r.hitCount}</td>
                    <td>{r.category ?? "\u2014"}</td>
                    <td className="learned-rules-panel__actions">
                      <button
                        type="button"
                        className="learned-rules-panel__action-btn"
                        aria-label={`Disable rule ${r.ruleId}`}
                        title="Disable this rule"
                        disabled={mutatingIds.has(r.ruleId)}
                        onClick={() => void handleDisable(r.ruleId)}
                      >
                        &#9646;&#9646;
                      </button>
                      <button
                        type="button"
                        className="learned-rules-panel__action-btn learned-rules-panel__action-btn--danger"
                        aria-label={`Delete rule ${r.ruleId}`}
                        title="Delete this rule"
                        disabled={mutatingIds.has(r.ruleId)}
                        onClick={() =>
                          setPendingDelete({ ruleId: r.ruleId, description: r.description })
                        }
                      >
                        &#128465;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {pendingDelete && (
        <div
          className="learned-rules-panel__confirm-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={`Delete rule ${pendingDelete.ruleId}`}
        >
          <div className="learned-rules-panel__confirm-box">
            <p className="learned-rules-panel__confirm-message">
              Delete rule &ldquo;{pendingDelete.description}&rdquo;? This cannot be undone.
            </p>
            <div className="learned-rules-panel__confirm-actions">
              <button
                type="button"
                className="learned-rules-panel__confirm-cancel"
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="learned-rules-panel__confirm-delete"
                onClick={() => void handleDeleteConfirm()}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
